import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@meitu/saas-ui';
import { ICustomNodeData } from '@meitu/saas-editor-core/es/framework';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { getImgInfoAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { sleep } from '@meitu/util';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useImageEditorModel } from '@/store/models/image-editor-model';
import ContrastView from './contrast-view';
import './contrast.less';
import IconFont from '@meitu/xdesign-icon-font';

const Contrast: React.FC = () => {
    const { $t } = useLocaleStore();
    const naturalImage = useImageEditorModel((state) => state.naturalImage);
    const setContrast = useImageEditorModel((state) => state.setContrast);
    const [naturalImageInfo, setNaturalImageInfo] = useState<{
        width: number;
        height: number;
        image: HTMLImageElement;
    } | null>(null);
    const isMouseDown = useRef(false);
    const contrastViewRef = useRef<ICustomNodeData | null>(null);

    useEffect(() => {
        (async function () {
            const code = naturalImage && base64Kit.getCode(naturalImage);
            const remoteUrl = await base64Kit.getRemoteUrlAsync(code as string);
            const { width, height, img: image } = await getImgInfoAsync(remoteUrl);
            setNaturalImageInfo({ width, height, image });
        })();
    }, [naturalImage]);

    const { width: stageWidth, height: stageHeight } = editor.services.frame.getSize();
    const layer = editor.mainLayer.getMainLayer();

    const handleMouseUp = useCallback(() => {
        if (isMouseDown.current) {
            setContrast({ isShow: false });
            isMouseDown.current = false;
            if (contrastViewRef.current) {
                editor.customLayer.remove(contrastViewRef.current);
                contrastViewRef.current = null;
            }
            editor.enableOperate();
        }
    }, []);

    const handleMouseDown = async () => {
        // if (isMouseDown.current) {
        //     handleMouseUp();
        //     return;
        // }
        isMouseDown.current = true;
        if (needAutoScale) {
            editor.setIsAutoScale(true);
            // 等待舞台自动缩放完成
            await sleep(0);
        }
        const { width: stageWidth, height: stageHeight } = editor.services.frame.getSize();

        const originIndex = editor.services.frame.getActiveFrameId();
        const viewWidth = editor.services.stage.getViewWidth();
        const viewHeight = editor.services.stage.getViewHeight();
        const scale = editor.getScale();

        const contrast = {
            width: finalWidth,
            height: finalHeight,
            isShow: true,
        };

        // console.log('Contrast', {
        //     finalWidth,
        //     finalHeight,
        //     finalScale,
        //     widthScale,
        //     heightScale,
        //     // imageScale,
        //     layerWidth,
        //     layerHeight,
        //     naturalWidth,
        //     naturalHeight,
        //     viewWidth,
        //     viewHeight,
        //     stageWidth,
        //     stageHeight,
        //     scale,
        // });

        setContrast(contrast);

        contrastViewRef.current = editor.customLayer.add(
            originIndex,
            'contrast',
            <ContrastView
                contrast={contrast}
                data={{
                    viewWidth,
                    viewHeight,
                    stageWidth,
                    stageHeight,
                    scale,
                    image,
                }}
            />,
            'top',
        );
        editor.services.frame.delActiveLayer();
        editor.disableOperate();
    };

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseUp]);

    if (!layer) return null;

    const { width: layerWidth, height: layerHeight } = layer!;

    const {
        width: naturalWidth,
        height: naturalHeight,
        image,
    } = naturalImageInfo || {
        width: 0,
        height: 0,
        image: new Image(),
    };

    const widthScale = layerWidth / naturalWidth;
    const heightScale = layerHeight / naturalHeight;
    const finalScale = Math.min(widthScale, heightScale);
    const finalWidth = naturalWidth * finalScale;
    const finalHeight = naturalHeight * finalScale;
    // 对比渲染宽高与舞台宽高不一致时，需要自动缩放
    const needAutoScale = finalWidth !== stageWidth || finalHeight !== stageHeight;

    if (!naturalImage) return null;

    return (
        <div className="l-navigator__contrast">
            <Tooltip placement="top" title={$t((v) => v.stage.toolbar.pressToView)} trigger="hover">
                <div className="l-navigator__contrast__content" onMouseDown={handleMouseDown}>
                    <IconFont name="CompareBold" fontSize={16} />
                    {$t((v) => v.stage.toolbar.contrast)}
                </div>
            </Tooltip>
        </div>
    );
};

export default React.memo(Contrast);
